import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
  className?: string;
}

export default function FAQSection({ faqs, title = 'Remodeling Questions Answered', className = '' }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, visible } = useScrollAnimation();

  return (
    <section
      className={`bg-[#F9F7F3] py-32 px-6 lg:px-16 xl:px-24 ${className}`}
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="max-w-6xl mx-auto">

        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-16 xl:gap-24">

          {/* Left sticky heading */}
          <div className="lg:sticky lg:top-28 self-start">
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">FAQ</p>
            <h2
              className="font-serif text-4xl lg:text-5xl text-stone-900 leading-tight mb-6"
              style={{ letterSpacing: '-0.02em' }}
            >
              {title}
            </h2>
            <p className="text-stone-500 text-sm leading-relaxed">
              Everything you need to know about starting a remodeling project in Brandon, FL and surrounding communities.
            </p>
            {/* Decorative line */}
            <div className="mt-8 w-12 h-px bg-accent" />
          </div>

          {/* Right: accordion */}
          <div
            ref={ref}
            className={`flex flex-col gap-1.5 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                  openIndex === i ? 'border-stone-200 bg-white' : 'border-stone-200/60 bg-white/60 hover:bg-white'
                }`}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <h3
                    className={`font-semibold text-sm md:text-base leading-snug transition-colors ${
                      openIndex === i ? 'text-stone-900' : 'text-stone-700'
                    }`}
                    itemProp="name"
                  >
                    {faq.q}
                  </h3>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                    openIndex === i ? 'bg-stone-950 rotate-45' : 'bg-stone-100'
                  }`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-add-line text-sm transition-colors ${openIndex === i ? 'text-white' : 'text-stone-500'}`} />
                    </div>
                  </div>
                </button>
                {openIndex === i && (
                  <div
                    className="px-6 pb-6"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <div className="h-px w-full bg-stone-100 mb-5" />
                    <p className="text-stone-600 text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
